// アカウントの出入り（ログアウト）と端末（パスキー）の管理。docs/AUTH.md の実装順序(7)。
//
// 🔴 **ログアウトはこの端末のローカル記録を必ず破棄する**。残したまま次のログインを許すと、
//    サーバー由来の記録がローカルにも残った状態で統合され、**回数や時間が二重に足される**
//    （統合規則の `clears` / `games` / `playMs` は合算）。
//    「ゲストのローカル記録は必ず純ローカル」という不変条件を、ここで維持する。

import { clearLocalPlayerData } from './playerId';

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';

/** 端末一覧の1行（＝ `auth_credentials` の1行）。 */
export interface DeviceRow {
  /** `credential_id`。削除の宛先。**自分のものしか返らない**（サーバー側で絞ってある）。 */
  id: string;
  /** 端末の呼び名（登録時の既定は UA 由来）。付いていないこともある。 */
  label: string | null;
  transports?: string[];
  createdAt: number;
  lastUsedAt: number;
  /** 今このブラウザのセッションを発行した鍵か。引き継ぎコードで入った場合は全部 false。 */
  current: boolean;
}

/** この端末が今ログインしているか。 */
export interface SessionState {
  authenticated: boolean;
  /** ログインしている場合、それが誰のセッションか。していなければ null。 */
  playerId: string | null;
}

/**
 * この端末のログイン状態を聞く。
 *
 * 🔴 **「登録済みなのにセッションが切れている」を画面が知るための唯一の手段**。
 *    ローカルの `hongsoft.nickname` が有るかどうかは**登録したことがあるか**しか表さず、
 *    今ログインしているかは表さない。両者を混同していたせいで、
 *    セッションが切れると入り直せない行き止まりができていた。
 *
 * ⚠️ 通信できないときは `authenticated: true` を返す（**オフラインを「切れている」と
 *    決めつけない**）。切れていないのに入り直しを促すと、押しても失敗する上に
 *    「勝手にログアウトされた」と受け取られる。**分からないときは何も出さない**方が安全。
 */
export async function fetchSessionState(): Promise<SessionState> {
  try {
    const res = await fetch(`${API_BASE}/auth/session`, { credentials: 'same-origin' });
    if (!res.ok) return { authenticated: true, playerId: null };
    const body = (await res.json()) as { authenticated?: unknown; playerId?: unknown };
    if (typeof body.authenticated !== 'boolean') return { authenticated: true, playerId: null };
    return {
      authenticated: body.authenticated,
      playerId: typeof body.playerId === 'string' ? body.playerId : null,
    };
  } catch {
    return { authenticated: true, playerId: null };
  }
}

/**
 * 「入り直してください」を出してよいか。
 *
 * 🔴 **出すのは「おかしいと分かったとき」だけ**。「分からない」を「切れている」の側に倒すと、
 *    オフラインの人に毎回ログインを促し、押しても失敗する＝「勝手にログアウトされた」に見える。
 *
 * ⚠️ **`playerId` が null なのは「別人」ではなく「分からない」**。ここを取り違えたのが
 *    2026-08-08 に踏んだバグで、`fetchSessionState` の失敗時の戻り
 *    （`{authenticated:true, playerId:null}`）が「別人のセッション」と判定され、
 *    **オフラインの人全員にボタンが出ていた**。
 */
export function shouldOfferResume(state: SessionState, localPlayerId: string): boolean {
  if (!state.authenticated) return true; // 切れていると分かった
  if (state.playerId === null) return false; // 分からない（通信失敗・応答が不正）
  return state.playerId !== localPlayerId; // 別人のセッションが載っている
}

/**
 * この端末からログアウトする。
 *
 * ⚠️ **ローカルの破棄まで含めて1つの操作**。サーバーの Cookie だけ消して戻ると、
 *    「ログアウトしたのに前の人の記録が残っている端末」になる。
 *    通信に失敗しても**ローカルは必ず消す**（出口を塞がないため）。
 *
 * 戻ったあとは `getPlayerId()` が**新しい匿名ID**を作る（`clearLocalPlayerData` が
 * `hongsoft.playerId` ごと消すため）。
 */
export async function logoutThisDevice(): Promise<void> {
  try {
    await fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: '{}',
    });
  } catch {
    // オフラインでもログアウトはできなければならない。Cookie はブラウザを閉じれば
    // どのみち送られなくなるし、ローカルを消せば「この端末の持ち主」は空になる。
  }
  clearLocalPlayerData();
}

/**
 * すべての端末からログアウトする（`players.token_version` を進める）。
 * 端末を紛失したときの操作。**セッションが要る**。
 *
 * @returns 切れたら true。セッションが無い・通信に失敗したら false（ローカルは触らない）
 */
export async function logoutAllDevices(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ allDevices: true }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * この人のパスキー一覧。**セッションが要る**（無ければ null）。
 * null は「取れなかった」＝ 画面では「先にログインしてください」と案内する。
 */
export async function fetchDevices(): Promise<DeviceRow[] | null> {
  try {
    const res = await fetch(`${API_BASE}/auth/devices`, { credentials: 'same-origin' });
    if (!res.ok) return null;
    const body = (await res.json()) as { devices?: unknown };
    return Array.isArray(body.devices) ? (body.devices as DeviceRow[]) : null;
  } catch {
    return null;
  }
}

/**
 * パスキーを1本消す。
 *
 * ⚠️ **サーバーの行を消すだけ**で、端末（iCloud キーチェーン等）に残っているパスキーは消えない。
 *    利用者は認証器の設定から自分で消すことになる。逆向き（端末から消してもサーバーに残る）が
 *    まさに「掃除できない残骸」の正体なので、**画面でそう説明すること**。
 */
export async function deleteDevice(credentialId: string): Promise<boolean> {
  try {
    const res = await fetch(
      `${API_BASE}/auth/devices?credentialId=${encodeURIComponent(credentialId)}`,
      { method: 'DELETE', credentials: 'same-origin' },
    );
    return res.ok;
  } catch {
    return false;
  }
}
